import type { Customer } from "@stb/api-contract"
import { Loader2 } from "lucide-react"
import { useState } from "react"

import { Button } from "@/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog"

import { describeRpcError } from "../../rpc-error"
import type { RpcResult } from "../../rpc/context"

export interface ConfirmDeleteCustomerProps {
  customer: Customer
  onCancel: () => void
  onDelete: (id: string) => Promise<RpcResult<null>>
  onDone: () => void
}

/** Confirmation before deleting a customer (existing invoices keep the buyer's name). */
export function ConfirmDeleteCustomer({
  customer,
  onCancel,
  onDelete,
  onDone,
}: ConfirmDeleteCustomerProps) {
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  async function confirm() {
    setPending(true)
    setError(null)
    try {
      const result = await onDelete(customer.id)
      if (result.ok) onDone()
      else setError(describeRpcError(result.error).message)
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog open onOpenChange={(next) => !next && !pending && onCancel()}>
      <DialogContent role="alertdialog">
        <DialogHeader>
          <DialogTitle>Delete {customer.name}?</DialogTitle>
          <DialogDescription>
            The customer is removed from your list. Invoices already issued to them are not changed.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Keep customer
          </Button>
          <Button variant="destructive" onClick={() => void confirm()} disabled={pending}>
            {pending ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            Delete customer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
